import {
    BymoPagePropsContentSettingsFilterOptions,
    bymoPagePropsFilterContentSettings
} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import {
    hideableValueCreateHidden,
    HtmlObjectHorizontalPosition,
    HtmlObjectVerticalPosition,
    objectOmit,
    PartiallyRequired,
    uuidCreate
} from "@wix/devzai-utils-common";
import {BymoPageActionMediaLayout, BymoPageActionMediaSize, BymoPageActionMediaSizing} from "../bymo-pages";
import {
    doppeListActionGetDefaultListSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration
} from "./doppe-list-action-settings";
import {DoppeActionLayout} from "./doppe-action-layout";
import {
    doppeActionTypeMediaGalleryResolveListItemSettings
} from "../../doppe-action-types/doppe-action-type-media-gallery/doppe-action-type-media-gallery-utils";
import {
    doppeActionTypeTestimonialsListResolveListItemSettings
} from "../../doppe-action-types/doppe-action-type-testimonials-list/doppe-action-type-testimonials-list-utils";
import {
    DoppeActionContentSection,
    DoppeActionContentSectionContent,
    DoppeActionContentSectionLayoutSettings,
    DoppeActionContentSectionLayoutType,
    DoppeActionContentSectionsGeneralSettingsMetadata,
    DoppeActionContentSectionsSettingsMetadata,
    DoppeActionContentSectionType
} from "./doppe-action-content-section";

export function doppeActionContentSectionLayoutCreateDefault(type: DoppeActionContentSectionLayoutType) {

    switch (type) {
        case DoppeActionContentSectionLayoutType.Inline: {
            return {
                type: DoppeActionContentSectionLayoutType.Inline,
                hideHeader: false,
            } satisfies DoppeActionContentSectionLayoutSettings.Inline;
        }
        case DoppeActionContentSectionLayoutType.Collapsable: {
            return {
                type: DoppeActionContentSectionLayoutType.Collapsable,
                isOpenByDefault: false,
            } satisfies DoppeActionContentSectionLayoutSettings.Collapsable;
        }
    }
}

export function doppeActionContentSectionFilterContentSettings(
    contentSection: DoppeActionContentSection,
    options: BymoPagePropsContentSettingsFilterOptions
) {
    return {
        ...bymoPagePropsFilterContentSettings(objectOmit(contentSection, ['id']), DoppeActionContentSectionsGeneralSettingsMetadata, options),
        settings: bymoPagePropsFilterContentSettings(contentSection.content, DoppeActionContentSectionsSettingsMetadata[contentSection.content.type], options)
    }
}

export function doppeActionContentSectionGetDefaultValues(type: DoppeActionContentSectionType) {

    const baseSettings = {
        isVisible: true,
        id: uuidCreate(),
        layoutSettings: {
            type: DoppeActionContentSectionLayoutType.Collapsable,
            isOpenByDefault: false
        },
        isRequired: false,
        title: ''
    }

    const partialSectionContentSettings = doppeActionContentSectionPartialCreateDefaultContentSettings(type);

    return {
        content: partialSectionContentSettings,
        ...baseSettings
    }  satisfies DoppeActionContentSection.Section;
}

export function doppeActionContentSectionPartialCreateDefaultContentSettings(type: DoppeActionContentSectionType): DoppeActionContentSectionContent {
    switch (type) {
        case DoppeActionContentSectionType.Image: {
            return {
                type: DoppeActionContentSectionType.Image,
                image: null,
                imageViewSettings: {
                    mediaLayout: BymoPageActionMediaLayout.Cropped,
                    mediaSizing: BymoPageActionMediaSizing.Custom,
                    size: BymoPageActionMediaSize.Square,
                    alignment: {
                        horizontal: HtmlObjectHorizontalPosition.Center,
                        vertical: HtmlObjectVerticalPosition.Center
                    }
                },
            } satisfies DoppeActionContentSection.ImageSettings;
        }
        case DoppeActionContentSectionType.Video: {
            return {
                type: DoppeActionContentSectionType.Video,
                video: null,
                autoPlay: true,
            } satisfies DoppeActionContentSection.VideoSettings;
        }
        case DoppeActionContentSectionType.Gallery: {
            return {
                type: DoppeActionContentSectionType.Gallery,
                ...doppeListActionGetDefaultListSettings({
                    layout: DoppeActionLayout.Carousel,
                    listDataItems: [],
                }),
                ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration({}, false),
                ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration({}),
            } satisfies DoppeActionContentSection.GallerySettings;
        }
        case DoppeActionContentSectionType.Testimonials: {
            return {
                type: DoppeActionContentSectionType.Testimonials,
                ...doppeListActionGetDefaultListSettings({
                    layout: DoppeActionLayout.Carousel,
                    listDataItems: [],
                }),
                ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration({}, false),
                ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration({}),
            } satisfies DoppeActionContentSection.TestimonialsSettings;
        }
        case DoppeActionContentSectionType.PropertyDetails: {
            return {
                type: DoppeActionContentSectionType.PropertyDetails,
                bedroomsNumber: hideableValueCreateHidden(3),
                bathroomsNumber: hideableValueCreateHidden(5),
                floors: hideableValueCreateHidden(1),
                yearBuilt: hideableValueCreateHidden(2000),
                houseArea: hideableValueCreateHidden(''),
                landArea: hideableValueCreateHidden(''),
            } satisfies DoppeActionContentSection.PropertyDetailsSettings
        }
        case DoppeActionContentSectionType.Map: {
            return {
                type: DoppeActionContentSectionType.Map,
                address: {
                    locationCoordinates: {
                        latitude: 0,
                        longitude: 0,
                    },
                    displayAddress: ''
                },
            } satisfies DoppeActionContentSection.MapSettings;
        }
        case DoppeActionContentSectionType.RichContent: {
            return {
                type: DoppeActionContentSectionType.RichContent,
                richText: ''
            } satisfies DoppeActionContentSection.RichTextSettings;
        }
    }
}

export function doppeActionContentSectionPartialCreateDefaultContentSettingsMigration(content: PartiallyRequired<DoppeActionContentSectionContent, 'type'>): DoppeActionContentSectionContent {
    switch (content.type) {
        case DoppeActionContentSectionType.Image: {

            const actualContent = content as Partial<DoppeActionContentSection.ImageSettings>;

            const {
                imageViewSettings,
                ...restContent
            } = actualContent ?? {};


            return {
                type: DoppeActionContentSectionType.Image,
                image: null,
                imageViewSettings: {
                    mediaLayout: BymoPageActionMediaLayout.Cropped,
                    mediaSizing: BymoPageActionMediaSizing.Custom,
                    size: BymoPageActionMediaSize.Square,
                    alignment: {
                        horizontal: HtmlObjectHorizontalPosition.Center,
                        vertical: HtmlObjectVerticalPosition.Center
                    },
                    ...imageViewSettings
                },
                ...restContent,
            } satisfies DoppeActionContentSection.ImageSettings;
        }
        case DoppeActionContentSectionType.Video: {

            const actualContent = content as Partial<DoppeActionContentSection.VideoSettings>;

            return {
                type: DoppeActionContentSectionType.Video,
                video: null,
                autoPlay: true,
                ...actualContent
            } satisfies DoppeActionContentSection.VideoSettings;
        }
        case DoppeActionContentSectionType.Gallery: {

            const actualContent = content as Partial<DoppeActionContentSection.GallerySettings>;

            const {
                listDataItems = [],
                listItemTemplateCtaButtonsSettings,
                listItemTemplateSectionsSettings,
                ...restContent
            } = actualContent ?? {};

            return {
                type: DoppeActionContentSectionType.Gallery,
                ...doppeListActionGetDefaultListSettings({
                    layout: DoppeActionLayout.Carousel,
                    listDataItems: listDataItems.map(doppeActionTypeMediaGalleryResolveListItemSettings)
                }),
                ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(actualContent, false),
                ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(actualContent),
                layout: DoppeActionLayout.Carousel,
                ...restContent
            } satisfies DoppeActionContentSection.GallerySettings;
        }
        case DoppeActionContentSectionType.Testimonials: {
            const actualContent = content as Partial<DoppeActionContentSection.TestimonialsSettings>;

            const {
                listDataItems = [],
                listItemTemplateCtaButtonsSettings,
                listItemTemplateSectionsSettings,
                ...restContent
            } = actualContent ?? {};

            return {
                type: DoppeActionContentSectionType.Testimonials,
                ...doppeListActionGetDefaultListSettings({
                    layout: DoppeActionLayout.Carousel,
                    listDataItems: listDataItems.map(doppeActionTypeTestimonialsListResolveListItemSettings)
                }),
                ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(actualContent, false),
                ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(actualContent),
                ...restContent
            } satisfies DoppeActionContentSection.TestimonialsSettings;
        }
        case DoppeActionContentSectionType.PropertyDetails: {

            const actualContent = content as Partial<DoppeActionContentSection.PropertyDetailsSettings>;

            return {
                type: DoppeActionContentSectionType.PropertyDetails,
                bedroomsNumber: hideableValueCreateHidden(3),
                bathroomsNumber: hideableValueCreateHidden(5),
                floors: hideableValueCreateHidden(1),
                yearBuilt: hideableValueCreateHidden(2000),
                houseArea: hideableValueCreateHidden(''),
                landArea: hideableValueCreateHidden(''),
                ...actualContent,
            } satisfies DoppeActionContentSection.PropertyDetailsSettings
        }
        case DoppeActionContentSectionType.RichContent: {
            const actualContent = content as Partial<DoppeActionContentSection.RichTextSettings>;

            return {
                type: DoppeActionContentSectionType.RichContent,
                richText: '',
                ...actualContent
            } satisfies DoppeActionContentSection.RichTextSettings;
        }
        case DoppeActionContentSectionType.Map: {
            const actualContent = content as Partial<DoppeActionContentSection.MapSettings>;

            return {
                type: DoppeActionContentSectionType.Map,
                address: {
                    locationCoordinates: {
                        latitude: 0,
                        longitude: 0,
                    },
                    displayAddress: '',
                },
                ...actualContent
            } satisfies DoppeActionContentSection.MapSettings;
        }
    }
}